// FILE: apps/admin/src/pages/Transactions.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Immutable RUB and GRAM transaction ledger explorer with currency, type, and correlationId/user filtering.
//   SCOPE: Transactions component.
//   DEPENDS: ../api.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   Transactions - Admin RUB & GRAM transaction ledger explorer
// END_MODULE_MAP

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { api, dt, gram, rub } from '../api.js';

interface TxRow {
  id: string;
  userId: string;
  telegramId: number | null;
  username: string | null;
  type: string;
  currency: 'RUB' | 'GRAM';
  amountKopecks: number;
  balanceBeforeKopecks: number;
  balanceAfterKopecks: number;
  referenceId: string | null;
  correlationId: string | null;
  description: string;
  createdAt: string;
}

const TX_TYPES = [
  'ALL',
  'deposit',
  'purchase',
  'bet',
  'win',
  'case_open',
  'case_reward',
  'referral',
  'admin_adjust',
] as const;

// START_CONTRACT: Transactions
//   PURPOSE: Render the filterable RUB & GRAM transaction ledger table.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Queries GET /admin/transactions
//   LINKS: M-ADMIN
// END_CONTRACT: Transactions
export function Transactions() {
  // START_BLOCK_TRANSACTIONS
  const [currency, setCurrency] = useState<'ALL' | 'RUB' | 'GRAM'>('ALL');
  const [type, setType] = useState<string>('ALL');
  const [q, setQ] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-transactions', currency, type, q],
    queryFn: () =>
      api.get<{ items: TxRow[] }>(
        `/admin/transactions?currency=${currency}&type=${type}&userId=${encodeURIComponent(q)}&limit=150`,
      ),
    refetchInterval: 15000,
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Леджер транзакций (RUB &amp; GRAM)</h1>
          <div className="page-subtitle">
            Неизменяемый журнал всех движений баланса с трассировкой до/после и correlationId
          </div>
        </div>
      </div>

      <div className="toolbar">
        <div className="tabs" style={{ marginBottom: 0 }}>
          {(['ALL', 'RUB', 'GRAM'] as const).map((c) => (
            <button
              key={c}
              type="button"
              className={`tab-btn ${currency === c ? 'active' : ''}`}
              onClick={() => setCurrency(c)}
            >
              {c === 'ALL' ? 'Все валюты' : c}
            </button>
          ))}
        </div>

        <select value={type} onChange={(e) => setType(e.target.value)}>
          {TX_TYPES.map((t) => (
            <option key={t} value={t}>
              {t === 'ALL' ? 'Все типы операций' : t}
            </option>
          ))}
        </select>

        <input
          style={{ flex: '1 1 220px', minWidth: 0 }}
          placeholder="Фильтр по описанию / referenceId / correlationId…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="muted">Загрузка журнала транзакций…</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Игрок</th>
                  <th>Валюта / Тип</th>
                  <th>Сумма</th>
                  <th>Баланс (До → После)</th>
                  <th>Описание / Correlation</th>
                  <th>Время</th>
                </tr>
              </thead>
              <tbody>
                {(data?.items ?? []).map((tx) => {
                  const isGram = tx.currency === 'GRAM';
                  const fmt = (v: number) => (isGram ? `${gram(v)} GRAM` : `${rub(v)} ₽`);
                  return (
                    <tr key={tx.id}>
                      <td>
                        <Link
                          to={`/users?userId=${encodeURIComponent(tx.userId)}`}
                          style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}
                        >
                          {tx.username ? `@${tx.username}` : `#${tx.telegramId ?? tx.userId.slice(0, 8)}`}
                        </Link>
                      </td>
                      <td>
                        <span className="chip info">{tx.currency}</span>{' '}
                        <span className="mono" style={{ fontSize: 12 }}>{tx.type}</span>
                      </td>
                      <td
                        className="mono"
                        style={{
                          fontWeight: 700,
                          color: tx.amountKopecks >= 0 ? 'var(--ok)' : 'var(--bad)',
                        }}
                      >
                        {tx.amountKopecks >= 0 ? '+' : ''}
                        {fmt(tx.amountKopecks)}
                      </td>
                      <td className="mono muted" style={{ fontSize: 12 }}>
                        {fmt(tx.balanceBeforeKopecks)} → {fmt(tx.balanceAfterKopecks)}
                      </td>
                      <td>
                        <div>{tx.description || '—'}</div>
                        {tx.correlationId && (
                          <div className="mono muted" style={{ fontSize: 11 }}>
                            corr: {tx.correlationId}
                          </div>
                        )}
                      </td>
                      <td className="muted" style={{ fontSize: 12 }}>{dt(tx.createdAt)}</td>
                    </tr>
                  );
                })}
                {!data?.items?.length && (
                  <tr>
                    <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 28 }}>
                      Транзакции не найдены
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
  // END_BLOCK_TRANSACTIONS
}
